// src/utils/apiHelper.js
import AuthService from '../services/authService';

/**
 * Centralized API helper that handles authentication, errors, and automatic logout
 * @param {string} endpoint - API endpoint (without base URL)
 * @param {object} options - Fetch options
 * @returns {Promise<{response: Response, data: any}>}
 */
export const apiRequest = async (endpoint, options = {}) => {
  const apiBase = window.APP_CONFIG?.apiUrl || 'https://api.dpg.net.tr';
  const url = `${apiBase}${endpoint}`;
  
  // Get auth token
  const token = AuthService.getAuthToken();
  
  // Prepare headers
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  // Add authorization header if auth is required (default: true)
  if (options.requireAuth !== false && token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Prepare fetch config
  const config = {
    ...options,
    headers,
  };
  
  try {
    const response = await fetch(url, config);
    
    // Handle 401 Unauthorized - Token expired or invalid
    if (response.status === 401 && options.requireAuth !== false) {
      console.warn('[API Helper] 401 Unauthorized - Token expired or invalid. Logging out...');
      
      // Clear auth data and redirect to login
      await AuthService.logout(false);
      
      return {
        response,
        data: null,
        error: {
          status: 401,
          message: 'Your session has expired. Please sign in again.',
          requiresLogout: true
        }
      };
    }
    
    // Parse response
    let data = null;
    const contentType = response.headers.get('content-type');
    
    if (contentType && contentType.includes('application/json')) {
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('[API Helper] JSON parse error:', parseError);
        data = null;
      }
    } else {
      // Try to get text if not JSON
      try {
        const text = await response.text();
        data = text;
      } catch (textError) {
        console.error('[API Helper] Text parse error:', textError);
      }
    }
    
    return {
      response,
      data,
      error: response.ok ? null : {
        status: response.status,
        statusText: response.statusText,
        message: data?.message || data?.Message || `Request failed with status ${response.status}`,
        data: data
      }
    };
  } catch (error) {
    console.error('[API Helper] Network error:', error);
    
    // Network errors - don't logout, just return error
    return {
      response: null,
      data: null,
      error: {
        status: 0,
        message: 'Network error. Please check your connection.',
        networkError: true,
        originalError: error
      }
    };
  }
};

/**
 * Simplified API request that returns success/error object
 * @param {string} endpoint - API endpoint
 * @param {object} options - Fetch options
 * @returns {Promise<{success: boolean, data?: any, message?: string, error?: any}>}
 */
export const apiRequestSimple = async (endpoint, options = {}) => {
  const result = await apiRequest(endpoint, options);
  
  // If logout was triggered, return early
  if (result.error?.requiresLogout) {
    return {
      success: false,
      message: result.error.message,
      requiresLogout: true
    };
  }
  
  // If there was an error
  if (result.error) {
    return {
      success: false,
      message: result.error.message,
      error: result.error,
      status: result.error.status
    };
  }
  
  // Success
  return {
    success: true,
    data: result.data?.data || result.data,
    message: result.data?.message || result.data?.Message,
    fullResponse: result.data
  };
};

export default apiRequest;

